import type { Building } from "building-types/building/buildingTypes";
import type { FloorSystem } from "building-types/floors/floorSystemTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Table } from "common/data/table";
import type { MaterialSrc } from "common/materials/materialSrc";
import { pathStringFromMaterialSrcWithUserDefinedName } from "common/materials/materialSrcFromPathString";
import type { Face } from "geom/brep";
import { faceArea } from "geom/brep";
import { DIST_EPSILON_SQUARED } from "geom/core";
import type { ConfigurableTable } from "lib/house/dataSequences/dataSequenceConfigurableTable";
import {
  aggregatorColumnNew,
  categoryColumnNew,
  configurableTableNew,
  stringColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";

interface FacesPerSrc {
  readonly faces: Face[];
  readonly srcHandle?: DocEntityHandle;
}
interface FloorMaterialPerLevel {
  readonly facesArrayPerSrc: FacesPerSrc[];
  area: number;
}

type FloorMaterialPerLevelSet = { [key: string]: FloorMaterialPerLevel };

interface StandardFloorMaterialKeyMeasure {
  readonly name: string;
  readonly levelsInfo: FloorMaterialPerLevelSet;
  area: number;
}

type StandardFloorMaterialKeyMeasureSet = { [key: string]: StandardFloorMaterialKeyMeasure };

function floorMaterialPerLevelNew(faces: Face[] = [], srcHandle?: DocEntityHandle): FloorMaterialPerLevel {
  let area = 0.0;
  for (const face of faces) {
    area += faceArea(face) / 144;
  }

  const facesPerSrcHandle: FacesPerSrc = { faces, srcHandle };

  return {
    facesArrayPerSrc: [facesPerSrcHandle],
    area,
  };
}

function standardFloorMaterialKeyMeasureNew(
  name: string,
  levelsInfo: FloorMaterialPerLevelSet = {},
): StandardFloorMaterialKeyMeasure {
  let area = 0.0;
  for (const levelId in levelsInfo) {
    area += levelsInfo[levelId].area;
  }

  return {
    name,
    levelsInfo,
    area,
  };
}

function floorMaterialPerLevelAdd(a: FloorMaterialPerLevel, b: FloorMaterialPerLevel) {
  a.area += b.area;
  a.facesArrayPerSrc.push(...b.facesArrayPerSrc);
}

function standardFloorMaterialKeyMeasureAdd(
  floorMaterialKeyMeasure: StandardFloorMaterialKeyMeasure,
  levelId: string,
  levelInfo: FloorMaterialPerLevel,
) {
  const { levelsInfo } = floorMaterialKeyMeasure;

  if (!levelsInfo[levelId]) {
    levelsInfo[levelId] = floorMaterialPerLevelNew();
  }

  floorMaterialPerLevelAdd(levelsInfo[levelId], levelInfo);
  floorMaterialKeyMeasure.area += levelInfo.area;
}

function isNonEmptyCounter(
  floorMaterialKeyMeasure: StandardFloorMaterialKeyMeasure,
): StandardFloorMaterialKeyMeasure | false {
  const { area = 0 } = floorMaterialKeyMeasure;

  if (area < DIST_EPSILON_SQUARED) {
    return false;
  }

  return floorMaterialKeyMeasure;
}

export function getFloorMaterialKeyMeasures(floorSystems: Table<FloorSystem>): StandardFloorMaterialKeyMeasureSet {
  const floorMaterialKeyMeasureSet: StandardFloorMaterialKeyMeasureSet = {};

  const addMaterialArea = (materialSrc: MaterialSrc, levelId: string, faces: Face[], srcHandle?: DocEntityHandle) => {
    const name = pathStringFromMaterialSrcWithUserDefinedName(materialSrc);
    if (!name) {
      return;
    }

    const floorMaterialKeyMeasure = floorMaterialKeyMeasureSet[name];
    const currentInfo = floorMaterialPerLevelNew(faces, srcHandle);

    if (!floorMaterialKeyMeasure) {
      floorMaterialKeyMeasureSet[name] = standardFloorMaterialKeyMeasureNew(name);
    }

    standardFloorMaterialKeyMeasureAdd(floorMaterialKeyMeasureSet[name], levelId, currentInfo);
  };

  for (const floorSystemId in floorSystems) {
    const floorSystem = floorSystems[floorSystemId];
    for (const floorFinishes of Object.values(floorSystem.roomFloorFinishes)) {
      for (const finish of floorFinishes) {
        const { finishFace, materialSrc: floorMat, srcHandle } = finish;
        addMaterialArea(floorMat, floorSystem.levelId, [finishFace], srcHandle);
      }
    }
  }

  return floorMaterialKeyMeasureSet;
}

export function makeFloorMaterialKeyMeasuresTable(building: Building): ConfigurableTable {
  const floorMaterialKeyMeasures = getFloorMaterialKeyMeasures(building.floorSystems);

  return configurableTableNew<StandardFloorMaterialKeyMeasure, StandardFloorMaterialKeyMeasure>({
    iterables: floorMaterialKeyMeasures,
    requirement: isNonEmptyCounter,
    columns: [
      categoryColumnNew("Floor Area"),
      stringColumnNew({
        name: "Material",
        fieldFunc: (counter) => counter.name,
      }),
    ],
    aggregator: aggregatorColumnNew({
      name: "Area (SF)",
      aggregatorFunc: (counter) => (counter.area ? Math.round(counter.area) : 0),
    }),
  });
}
